import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useGasStation } from '../GasStationsLayout';
import { useRealtimeStore, type DriverLocationData } from '@/stores';
import { DeliveryMap, type DriverMarkerData, type GasStationMarkerData, type CustomerMarkerData } from '@/components/DeliveryMap';
import { Badge } from '@/components/ui/badge';
import { Truck, Package, CircleDot, User, ClipboardList, Phone, MapPin, ShoppingCart } from 'lucide-react';
import { getAddress, listInventory, listOrders } from '@vn-propane/shared/client';
import type { InventoryWithProduct, OrderSummary } from '@vn-propane/shared/client';
import { formatPrice } from '@/lib/utils';

function formatQuantity(quantity: string) {
  const num = parseFloat(quantity);
  return num % 1 === 0 ? num.toString() : num.toFixed(2);
}

const GasStationDashboard = observer(function GasStationDashboard() {
  const { t } = useTranslation('admin');
  const { id: orgId, stationId } = useParams<{ id: string; stationId: string }>();
  const { station, organization } = useGasStation();
  const realtimeStore = useRealtimeStore();
  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [driverLocations, setDriverLocations] = useState<DriverLocationData[]>([]);
  const [inventory, setInventoryState] = useState<InventoryWithProduct[]>([]);
  const [activeOrders, setActiveOrders] = useState<OrderSummary[]>([]);
  const [pendingOrders, setPendingOrders] = useState<OrderSummary[]>([]);
  const [, setTick] = useState(0);

  function formatTimeAgo(timestamp: string): string {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) return t('stationDashboard.justNow');
    if (diffSecs < 120) return t('stationDashboard.oneMinAgo');
    if (diffSecs < 3600) return t('stationDashboard.minsAgo', { count: Math.floor(diffSecs / 60) });
    if (diffSecs < 7200) return t('stationDashboard.oneHourAgo');
    return t('stationDashboard.hoursAgo', { count: Math.floor(diffSecs / 3600) });
  }

  // Update timestamps every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch inventory for this station
  const fetchInventory = useCallback(async () => {
    if (!orgId || !stationId) return;
    try {
      const result = await listInventory({
        path: { org_id: orgId, station_id: stationId },
      });
      if (result.data) {
        setInventoryState(result.data.data);
      }
    } catch (err) {
      console.error('Failed to load inventory:', err);
    }
  }, [orgId, stationId]);

  // Fetch orders for the organization
  const fetchOrders = useCallback(async () => {
    if (!orgId) return;
    try {
      const result = await listOrders({
        path: { org_id: orgId },
      });
      if (result.data) {
        const stationOrders = result.data.data.filter(
          (o) => o.gas_station_name === station?.name
        );
        // Active orders: assigned/in_progress/arrived
        setActiveOrders(
          stationOrders.filter(
            (o) => o.status === 'assigned' || o.status === 'in_progress' || o.status === 'arrived'
          )
        );
        // Pending orders: not yet assigned to a driver
        setPendingOrders(
          stationOrders.filter((o) => o.status === 'pending')
        );
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
    }
  }, [orgId, station?.name]);

  useEffect(() => {
    fetchInventory();
    fetchOrders();
  }, [fetchInventory, fetchOrders]);

  // Connect to realtime for driver locations
  useEffect(() => {
    if (!organization?.id) return;

    // Connect to WebSocket using API base URL
    const apiBaseUrl = import.meta.env.VITE_API_URL;
    if (!apiBaseUrl) {
      console.error('[WebSocket] VITE_API_URL is not set');
      return;
    }
    realtimeStore.connect(organization.id, apiBaseUrl);

    // Subscribe to driver location updates
    const unsubscribe = realtimeStore.onDriverLocation((location) => {
      setDriverLocations((prev) => {
        // Update existing or add new
        const existing = prev.findIndex((l) => l.driver_id === location.driver_id);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = location;
          return updated;
        }
        return [...prev, location];
      });
    });

    // Refresh orders on create/dispatch/status changes
    const unsubCreated = realtimeStore.onOrderCreated(() => {
      fetchOrders();
    });
    const unsubDispatched = realtimeStore.onOrderDispatched(() => {
      fetchOrders();
    });
    const unsubStatus = realtimeStore.onOrderStatusUpdated(() => {
      fetchOrders();
    });

    // Initial load of driver locations from store
    setDriverLocations(realtimeStore.getDriverLocationsArray());

    return () => {
      unsubscribe();
      unsubCreated();
      unsubDispatched();
      unsubStatus();
    };
  }, [organization?.id, realtimeStore, fetchOrders]);

  // Update driver locations when store changes
  useEffect(() => {
    setDriverLocations(realtimeStore.getDriverLocationsArray());
  }, [realtimeStore.driverLocations.size]);

  // Fetch address coordinates if station has addressId
  useEffect(() => {
    async function fetchAddress() {
      if (!orgId || !station?.addressId) {
        setCoordinates([10.8231, 106.6297]); // Default to Ho Chi Minh City
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await getAddress({
          path: { org_id: orgId, id: station.addressId },
        });

        if (result.data?.data) {
          const addr = result.data.data;
          if (addr.latitude && addr.longitude) {
            setCoordinates([addr.latitude, addr.longitude]);
          } else {
            setError('Address has no coordinates');
            setCoordinates([10.8231, 106.6297]);
          }
        } else {
          setError('Could not load address');
          setCoordinates([10.8231, 106.6297]);
        }
      } catch (err) {
        console.error('Failed to fetch address:', err);
        setError('Could not load address');
        setCoordinates([10.8231, 106.6297]);
      } finally {
        setLoading(false);
      }
    }

    fetchAddress();
  }, [orgId, station?.addressId]);

  if (!station) {
    return null;
  }

  // Build a map from order_id -> order for driver lookup
  const orderByDriver = new Map<string, OrderSummary>();
  for (const order of activeOrders) {
    if (order.driver_name) {
      // Find the driver location that has this order
      const driverLoc = driverLocations.find((d) => d.current_order_id === order.id);
      if (driverLoc) {
        orderByDriver.set(driverLoc.driver_id, order);
      }
    }
  }

  // Convert driver locations to map marker format
  const driverMarkers: DriverMarkerData[] = driverLocations.map((loc) => ({
    id: loc.driver_id,
    name: loc.driver_name || t('stationDashboard.driver'),
    latitude: loc.latitude,
    longitude: loc.longitude,
    isOnDelivery: !!loc.current_order_id,
    lastUpdated: loc.timestamp,
  }));

  // Convert gas station to map marker format
  const gasStationMarkers: GasStationMarkerData[] = coordinates
    ? [
        {
          id: station.id,
          name: station.name,
          latitude: coordinates[0],
          longitude: coordinates[1],
        },
      ]
    : [];

  // Build customer markers from active orders that have addresses
  const customerMarkers: CustomerMarkerData[] = [];
  // Note: OrderSummary has customer_address but no coordinates,
  // so we can't place them on the map from this data alone.

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:h-[calc(100vh-16rem)]">
      {/* Left panel: Inventory + Drivers */}
      <div className="lg:w-1/2 flex flex-col gap-6 min-w-0 lg:overflow-y-auto lg:pr-1 shrink-0">
        {/* Inventory summary */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('stationDashboard.inventory')}
            </h3>
          </div>
          {inventory.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">{t('stationDashboard.product')}</th>
                    <th className="text-right px-3 py-2 font-medium">{t('stationDashboard.inStock')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {inventory.map((item) => {
                    const qty = parseFloat(item.quantity);
                    const maxCap = item.max_capacity ? parseFloat(item.max_capacity) : null;
                    const fillPct = maxCap && maxCap > 0 ? Math.min((qty / maxCap) * 100, 100) : null;
                    const isEmpty = qty <= 0;
                    return (
                      <tr key={item.id} className="hover:bg-muted/30">
                        <td className="px-3 py-2">
                          <span className="font-medium">{item.product_name}</span>
                        </td>
                        <td className="px-3 py-2">
                          {fillPct !== null ? (
                            <div className="flex items-center gap-2 justify-end">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    fillPct > 25
                                      ? 'bg-emerald-500'
                                      : fillPct > 10
                                        ? 'bg-amber-500'
                                        : 'bg-red-500'
                                  }`}
                                  style={{ width: `${fillPct}%` }}
                                />
                              </div>
                              <span className={`text-xs whitespace-nowrap ${isEmpty ? 'text-red-600 font-semibold' : item.alert_threshold && qty <= parseFloat(item.alert_threshold) ? 'text-amber-600 font-medium' : ''}`}>
                                {formatQuantity(item.quantity)} / {formatQuantity(item.max_capacity!)} {item.product_unit}
                              </span>
                            </div>
                          ) : (
                            <span
                              className={`block text-right ${
                                isEmpty
                                  ? 'text-red-600 font-semibold'
                                  : item.alert_threshold && qty > 0 && qty <= parseFloat(item.alert_threshold)
                                    ? 'text-amber-600 font-medium'
                                    : ''
                              }`}
                            >
                              {formatQuantity(item.quantity)} {item.product_unit}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground border rounded-lg px-4 py-6 text-center">
              {t('stationDashboard.noInventoryTracked')}
            </div>
          )}
        </div>

        {/* Driver status */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t('stationDashboard.drivers')}
              </h3>
            </div>
            {driverLocations.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {t('stationDashboard.active', { count: driverLocations.length })}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {driverLocations.length > 0 ? (
              driverLocations.map((location) => {
                const order = orderByDriver.get(location.driver_id);
                const isOnDelivery = !!location.current_order_id;

                return (
                  <div
                    key={location.driver_id}
                    className="p-3 border rounded-lg bg-card"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-propane-100 flex items-center justify-center shrink-0">
                        <Truck className="w-4 h-4 text-propane-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {location.driver_name || t('stationDashboard.driver')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTimeAgo(location.timestamp)}
                        </div>
                      </div>
                      {isOnDelivery ? (
                        <Badge variant="default" className="shrink-0 text-xs">
                          {t('stationDashboard.onDelivery')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          <CircleDot className="w-3 h-3 mr-1" />
                          {t('stationDashboard.available')}
                        </Badge>
                      )}
                    </div>
                    {/* Delivery details */}
                    {isOnDelivery && order && (
                      <div className="mt-2 ml-11 text-xs space-y-1 text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <User className="w-3 h-3 shrink-0" />
                          <span className="truncate">
                            {order.customer_name || t('stationDashboard.unknownCustomer')}
                            {order.customer_phone && (
                              <span className="ml-1">({order.customer_phone})</span>
                            )}
                          </span>
                        </div>
                        {order.customer_address && (
                          <div className="truncate pl-[18px]">{order.customer_address}</div>
                        )}
                        <div className="pl-[18px]">
                          {t('stationDashboard.item', { count: order.item_count })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-muted-foreground text-center py-8 border rounded-lg">
                {t('stationDashboard.noActiveDrivers')}
              </div>
            )}
          </div>
        </div>

        {/* Outstanding orders */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t('stationDashboard.outstandingOrders')}
              </h3>
            </div>
            {pendingOrders.length > 0 && (
              <span className="text-xs text-amber-600 font-medium">
                {t('stationDashboard.pending', { count: pendingOrders.length })}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {pendingOrders.length > 0 ? (
              pendingOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/organizations/${orgId}/orders/${order.id}`}
                  className="block p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm font-mono">
                          #{order.id.slice(-8).toUpperCase()}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {t('stationDashboard.pendingBadge')}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        {order.customer_name && (
                          <div className="flex items-center gap-1.5">
                            <User className="w-3 h-3 shrink-0" />
                            <span className="truncate">{order.customer_name}</span>
                          </div>
                        )}
                        {order.customer_phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3 h-3 shrink-0" />
                            <span>{order.customer_phone}</span>
                          </div>
                        )}
                        {order.customer_address && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{order.customer_address}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <ShoppingCart className="w-3 h-3 shrink-0" />
                          <span>
                            {t('stationDashboard.item', { count: order.item_count })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-medium text-sm">
                        {formatPrice(order.total_amount.toString(), organization.currency)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatTimeAgo(order.created_at)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="text-sm text-muted-foreground text-center py-6 border rounded-lg">
                {t('stationDashboard.noOutstandingOrders')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right panel: Map */}
      <div className="lg:w-1/2 flex flex-col min-w-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('stationDashboard.map')}
          </h3>
        </div>
        <div className="h-[350px] lg:flex-1 lg:h-auto lg:min-h-[300px] rounded-lg overflow-hidden border bg-muted relative">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-muted-foreground">{t('stationDashboard.loadingMap')}</span>
            </div>
          ) : coordinates ? (
            <DeliveryMap
              drivers={driverMarkers}
              gasStations={gasStationMarkers}
              customers={customerMarkers}
              className="h-full w-full"
            />
          ) : null}
          {error && (
            <div className="absolute bottom-2 left-2 right-2 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default GasStationDashboard;
